import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ArrowUpDown, ArrowUp, ArrowDown, Pencil, Trash2, Eye } from 'lucide-react';

/**
 * columns: [{ key, header, align?: 'left'|'right', sortable?, render?: (row)=>node, sortValue?: (row)=>any, hideInDetail?: bool }]
 * rows: array of objects
 * actions: { onEdit?(row), onDelete?(row), canWrite? }
 * viewable: se true (default) mostra una "lente" per aprire il dettaglio completo della riga in sola lettura.
 */
export const DataTable = ({ columns, rows, actions, getRowId = (r) => r.id, emptyText = 'Nessun dato disponibile.', totals = null, viewable = true }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [viewRow, setViewRow] = useState(null);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    const val = col?.sortValue || ((r) => r[sortKey]);
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [rows, sortKey, sortDir, columns]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const canEditDelete = actions && (actions.onEdit || actions.onDelete) && actions.canWrite;
  const hasActions = viewable || canEditDelete;

  // Colonne mostrate nel dettaglio (esclude la lente stessa, il toggle export e ciò che è marcato hideInDetail)
  const detailColumns = columns.filter((c) => !c.hideInDetail && c.key !== 'export_flag');

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <Table data-testid="data-table">
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              {columns.map((c) => (
                <TableHead key={c.key} className={`text-xs font-semibold text-muted-foreground ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.sortable !== false ? (
                    <button className="inline-flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => toggleSort(c.key)} data-testid="data-table-sort">
                      <span>{c.header}</span>
                      {sortKey === c.key ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                    </button>
                  ) : c.header}
                </TableHead>
              ))}
              {hasActions && <TableHead className="text-right text-xs font-semibold text-muted-foreground w-[130px]">Azioni</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (hasActions ? 1 : 0)} className="text-center text-sm text-muted-foreground py-10">{emptyText}</TableCell>
              </TableRow>
            ) : sorted.map((row) => (
              <TableRow key={getRowId(row)} className="hover:bg-muted/50 transition-colors" data-testid="data-table-row">
                {columns.map((c) => (
                  <TableCell key={c.key} className={`text-sm py-3 ${c.align === 'right' ? 'text-right tabular' : ''}`}>
                    {c.render ? c.render(row) : row[c.key]}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {viewable && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewRow(row)} data-testid="data-table-view-button" title="Visualizza dettaglio" aria-label="Visualizza dettaglio">
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {canEditDelete && actions.onEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => actions.onEdit(row)} data-testid="data-table-edit-button" title="Modifica"><Pencil className="h-4 w-4" /></Button>}
                      {canEditDelete && actions.onDelete && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => actions.onDelete(row)} data-testid="data-table-delete-button" title="Elimina"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {totals && sorted.length > 0 && (
              <TableRow className="bg-muted/60 hover:bg-muted/60 border-t-2 border-border font-semibold" data-testid="data-table-totals">
                {columns.map((c, i) => (
                  <TableCell key={c.key} className={`text-sm py-3 ${c.align === 'right' ? 'text-right tabular' : ''}`}>
                    {totals[c.key] !== undefined ? totals[c.key] : (i === 0 ? 'Totale' : '')}
                  </TableCell>
                ))}
                {hasActions && <TableCell />}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dettaglio riga (sola lettura) — utile su mobile e per i profili senza permessi di modifica */}
      <Dialog open={!!viewRow} onOpenChange={(o) => { if (!o) setViewRow(null); }}>
        <DialogContent className="max-w-md w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="data-table-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-primary" />Dettaglio voce
            </DialogTitle>
          </DialogHeader>
          {viewRow && (
            <dl className="divide-y divide-border rounded-lg border">
              {detailColumns.map((c) => {
                const value = c.render ? c.render(viewRow) : viewRow[c.key];
                const isEmpty = value === null || value === undefined || value === '';
                return (
                  <div key={c.key} className="grid grid-cols-3 gap-3 px-3 py-2.5" data-testid={`detail-row-${c.key}`}>
                    <dt className="col-span-1 text-xs font-medium text-muted-foreground break-words">{c.header}</dt>
                    <dd className={`col-span-2 text-sm break-words ${c.align === 'right' ? 'tabular-nums' : ''}`}>
                      {isEmpty ? <span className="text-muted-foreground">—</span> : value}
                    </dd>
                  </div>
                );
              })}
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
